import { LOGISTICS_INTEGER_MAX } from '@gozoki/api-types/src/validation/logistics'
import type { UseQueryResult } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { z } from 'zod'
import { Button } from '~/components/ui/button'
import type { AppFormApi } from '~/hooks/form-hooks'
import { cn, getTuyauErrorStatus } from '~/lib/utils'
import type { RecordOrigin } from '~/routes/_authenticated/logistics/-record-origin'
import type { LogisticsSearch } from '~/routes/_authenticated/logistics/-search-schema'
import type { PermissionRequirement } from '~/utils/rbac'
import { getRbacMissingPermissions } from '~/utils/rbacQueryErrors'
import { LogisticsLayout, useOpenLogisticsList } from './logistics-layout'
import { LogisticsRouteGuard } from './logistics-route-guard'
import { MutationError, QueryError } from './logistics-shared'
import {
  LOGISTICS_PRIMARY_BUTTON_CLASS,
  LOGISTICS_SECONDARY_BUTTON_CLASS,
} from './logistics-styles'
import { HeaderDivider } from './logistics-ui'
import { RecordOriginBackLink, RecordOriginLink } from './record-origin-link'

/** Fiche d'un référentiel logistique (camion, hub) : cadre de route et chargement communs. */

/**
 * Cadre des routes d'une fiche (`new`, `$id`) : en-tête et barre d'onglets de la page Logistique,
 * onglet `tab` actif. Changer d'onglet mène à la page Logistique, avec les filtres de la liste
 * (`search`, déjà validé par la route), que la fiche reçoit aussi.
 */
export function LogisticsRecordRoute({
  tab,
  search,
  permission,
  children,
}: {
  tab: LogisticsSearch['tab']
  search: LogisticsSearch
  permission: PermissionRequirement
  children: (search: LogisticsSearch) => ReactNode
}) {
  const tabSearch: LogisticsSearch = { ...search, tab }
  const openList = useOpenLogisticsList(tabSearch)

  return (
    <LogisticsRouteGuard permission={permission}>
      <LogisticsLayout search={tabSearch} onSearchChange={openList} mockupBackground>
        {children(tabSearch)}
      </LogisticsLayout>
    </LogisticsRouteGuard>
  )
}

/**
 * Nouvel essai de lecture d'une fiche : la règle par défaut (main.tsx : pas de nouvel essai sur un
 * refus de droits), plus le 404 — un enregistrement supprimé ou un identifiant illisible ne
 * réapparaîtra pas en réessayant.
 */
export const retryRecordQuery = (failureCount: number, error: unknown) =>
  getTuyauErrorStatus(error) !== 404 &&
  getRbacMissingPermissions(error) === null &&
  failureCount < 3

/**
 * Chargement d'une fiche avant son formulaire. La relecture est attendue même si une copie est en
 * cache : le formulaire part de l'enregistrement à jour (sa référence sert au verrou optimiste).
 * Une relecture en échec garde les données : la fiche reste montée (saisie conservée), l'erreur
 * n'est affichée en pleine page que si l'enregistrement n'a jamais pu être lu.
 */
export function LogisticsRecordLoader<TRecord extends { id: number }>({
  query,
  labels,
  origin,
  children,
}: {
  query: UseQueryResult<TRecord>
  /** Écran d'où la fiche a été ouverte (LU-181) : son retour reste offert si elle est illisible. */
  origin?: RecordOrigin
  labels: {
    /** « Ce camion n’existe pas ou a été supprimé. » */
    notFound: string
    /** « Camion indisponible » */
    unavailable: string
    /** « Relecture du camion impossible, les valeurs affichées peuvent dater » */
    stale: string
  }
  children: (record: TRecord) => ReactNode
}) {
  if (query.isLoading || !query.isFetchedAfterMount) {
    return <p className="text-sm text-muted-foreground">Chargement…</p>
  }
  if (query.error && query.data === undefined) {
    const error =
      getTuyauErrorStatus(query.error) === 404 ? (
        <p className="text-sm text-muted-foreground">{labels.notFound}</p>
      ) : (
        <QueryError error={query.error} label={labels.unavailable} />
      )
    if (origin === undefined) return error
    return (
      <div className="flex flex-col items-start gap-2">
        <RecordOriginBackLink origin={origin} />
        {error}
      </div>
    )
  }
  if (query.data === undefined) return null
  return (
    <>
      <QueryError error={query.isRefetchError ? query.error : null} label={labels.stale} />
      {children(query.data)}
    </>
  )
}

/**
 * Identifiant d'une fiche lu dans l'URL (`$hubId`, `$vehicleId`) : un identifiant illisible (ou
 * hors des ids possibles) tombe sur « n'existe pas », aucun enregistrement n'ayant l'id 0 (404).
 */
export const logisticsRecordIdParam = () =>
  z.coerce.number().int().positive().max(LOGISTICS_INTEGER_MAX).catch(0)

/** Formulaire d'une fiche : l'envoi (bouton ou Entrée) passe par TanStack Form. */
export function LogisticsRecordForm({
  form,
  className,
  children,
}: {
  form: { handleSubmit: () => Promise<void> }
  /** Espacement propre à une maquette (« gap-2 » pour le camion). */
  className?: string
  children: ReactNode
}) {
  return (
    <form
      className={cn('flex flex-col gap-3', className)}
      onSubmit={(event) => {
        event.preventDefault()
        event.stopPropagation()
        void form.handleSubmit()
      }}
    >
      {children}
    </form>
  )
}

/**
 * Actions de l'en-tête d'une fiche, pour qui peut l'enregistrer : Annuler (retour, sans rien
 * enregistrer, à l'écran d'origine `origin` s'il y en a un, LU-181, sinon à la liste), Enregistrer
 * (désactivé tant que rien n'a changé), refus du serveur dessous.
 */
export function LogisticsRecordActions<TValues>({
  form,
  hasChanges,
  mutationError,
  search,
  origin,
}: {
  form: AppFormApi<TValues>
  hasChanges: boolean
  mutationError: unknown
  search: LogisticsSearch
  origin?: RecordOrigin
}) {
  return (
    <>
      <HeaderDivider />
      <div className="flex flex-col items-end gap-2">
        <div className="flex gap-2">
          <Button
            variant="ghost"
            className={cn(LOGISTICS_SECONDARY_BUTTON_CLASS, 'w-[180px]')}
            nativeButton={false}
            render={
              origin === undefined ? (
                <Link to="/logistics" search={search} />
              ) : (
                <RecordOriginLink origin={origin} />
              )
            }
          >
            Annuler
          </Button>
          <form.AppForm>
            <form.SubmitButton
              disabled={!hasChanges}
              className={cn(LOGISTICS_PRIMARY_BUTTON_CLASS, 'w-[180px]')}
            >
              Enregistrer
            </form.SubmitButton>
          </form.AppForm>
        </div>
        <MutationError error={mutationError} />
      </div>
    </>
  )
}
